import React, { useState, useEffect } from 'react';
import domain from '../../../../reusecomponent/domain';
import { getToken } from '../../../function/token';

export default function NodeEditModal({ open, onClose, node, onSave }) {
  const [formData, setFormData] = useState({
    message: '',
    keywords: [],
    type: 'message',
    condition: '',
    action: '',
    template: null
  });
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');

  useEffect(() => {
    if (open && node) {
      setFormData({
        message: node.data.message || '',
        keywords: node.data.keywords || [],
        type: node.data.type || 'message',
        condition: node.data.condition || '',
        action: node.data.action || '',
        template: node.data.selectedTemplate || null
      });
    }
  }, [open, node]);

  useEffect(() => {
    if (open) {
      loadTemplates();
    }
  }, [open]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await fetch(`${domain()}/manage-template`, {
        headers: { Authorization: token ? `Bearer ${token}` : '' }
      });
      const result = await response.json();
      if (result.status && Array.isArray(result.data)) {
        setTemplates(result.data);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave(node.id, formData);
    }
    onClose();
  };

  // Button functionality removed - buttons handled by dedicated button nodes

  const addKeyword = () => {
    if (newKeyword.trim() && !formData.keywords.includes(newKeyword.trim())) {
      setFormData(prev => ({
        ...prev,
        keywords: [...prev.keywords, newKeyword.trim()]
      }));
      setNewKeyword('');
    }
  };

  const removeKeyword = (index) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter((_, i) => i !== index)
    }));
  };

  if (!open) return null;

  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  };

  const contentStyle = {
    background: '#ffffff',
    borderRadius: '8px',
    padding: '24px',
    minWidth: '500px',
    maxWidth: '600px',
    maxHeight: '80vh',
    overflowY: 'auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
  };

  const inputStyle = {
    width: '100%',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    padding: '8px 12px',
    fontSize: '14px',
    marginBottom: '12px',
    outline: 'none'
  };

  const buttonStyle = {
    background: '#007bff',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer',
    marginRight: '8px'
  };

  const tagStyle = {
    background: '#e9ecef',
    color: '#495057',
    borderRadius: '4px',
    padding: '4px 8px',
    fontSize: '12px',
    marginRight: '6px',
    marginBottom: '6px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px'
  };

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={contentStyle} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, color: '#495057' }}>Configure Node</h3>
          <button 
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6c757d' }}
          >
            ×
          </button>
        </div>

        {/* Node Type */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Node Type</label>
          <select
            value={formData.type}
            onChange={e => setFormData(prev => ({ ...prev, type: e.target.value }))}
            style={inputStyle}
          >
            <option value="start">Start Bot</option>
            <option value="message">Message</option>
            <option value="condition">Condition</option>
            <option value="action">Action</option>
          </select>
        </div>

        {/* Keywords (for start node) */}
        {formData.type === 'start' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Trigger Keywords</label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input
                type="text"
                value={newKeyword}
                onChange={e => setNewKeyword(e.target.value)}
                placeholder="Enter keyword"
                style={{ ...inputStyle, marginBottom: 0 }}
                onKeyPress={e => e.key === 'Enter' && addKeyword()}
              />
              <button onClick={addKeyword} style={buttonStyle}>Add</button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {formData.keywords.map((keyword, index) => (
                <span key={index} style={tagStyle}>
                  {keyword}
                  <button
                    onClick={() => removeKeyword(index)}
                    style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '14px' }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Message */}
        {formData.type === 'message' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Message</label>
            <textarea
              value={formData.message}
              onChange={e => setFormData(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Enter your message..."
              style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
            />
          </div>
        )}

        {/* Condition */}
        {formData.type === 'condition' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Condition</label>
            <textarea
              value={formData.condition}
              onChange={e => setFormData(prev => ({ ...prev, condition: e.target.value }))}
              placeholder="Enter condition logic..."
              style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
            />
          </div>
        )}

        {/* Action */}
        {formData.type === 'action' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Action</label>
            <textarea
              value={formData.action}
              onChange={e => setFormData(prev => ({ ...prev, action: e.target.value }))}
              placeholder="Enter action to perform..."
              style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
            />
          </div>
        )}

        {/* Buttons section removed - buttons handled by dedicated button nodes */}

        {/* Template Selection */}
        {formData.type === 'message' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Template (Optional)</label>
            <select
              value={formData.template?.id || ''}
              onChange={e => {
                const selected = templates.find(t => t.id === e.target.value);
                setFormData(prev => ({ ...prev, template: selected || null }));
              }}
              style={inputStyle}
            >
              <option value="">Select a template</option>
              {templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name} - {template.language}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
          <button
            onClick={onClose}
            style={{ ...buttonStyle, background: '#6c757d' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={buttonStyle}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
} 